import _ from 'lodash';
import React from 'react';
import PropTypes from 'prop-types';
import {push} from 'react-router-redux';
import {createSelector} from 'reselect';
import queryString from 'query-string';
import {Checkbox} from 'rmwc';

import {request} from '../../containers/actions';
import Mark from '../marker/mark';
import Tree from '../tree';
import {removeItemSelection} from '../tree/actions';
import {refresh as paginatorRefresh, removeItem, clearSelection} from '../../paginator/actions';
import PaginatedContainer from '../../paginator/paginated_container';


export default class Results extends React.PureComponent {
    static contextTypes = {
        store: PropTypes.object,
    }

    static propTypes = {
        readonly: PropTypes.bool,
        selectable: PropTypes.bool,
        actions: PropTypes.array,
    }

    static defaultProps = {
        readonly: false,
        selectable: false,
        actions: [],
    }

    style = {items: {itemsList: {width: 'auto', minWidth: '100%', overflowX: 'auto'}}}

    listFields = [
        {field: 'id', label: window.gettext('ID')},
        {
            field: item => _.map(
                item.answer.examination.grades,
                grade => grade.title,
            ).join(window.gettext(',') + ' ') + ' - ' + item.answer.examination.title,
            label: window.gettext('Examination'),
        },
        {
            field: item => (item.answer.index + 1).persian(),
            label: window.gettext('Question Index'),
        },
        {
            field: item => `${item.marker.firstname} ${item.marker.lastname}`,
            label: window.gettext('Marker'),
        },
        {
            field: item => `${item.answer.student.firstname} ${item.answer.student.lastname}`,
            label: window.gettext('Student'),
        },
        {field: 'mark', label: window.gettext('Score')},
        {field: item => (item.answer.student.national_id || '').persian(), label: window.gettext('National ID')},
        {
            field: item => item.timestamp.toJalali().persian('YYYY/MM/DD - hh:mm:ss'),
            label: window.gettext('Date'),
        },
        {
            field: item => Number.parseInt(item.time_spent).persian() + ' ' + window.gettext('Seconds'),
            label: window.gettext('Time Spent'),
        },
        {
            field: item => item.answer.student.grade ? item.answer.student.grade.title : '-',
            label: window.gettext('Grade'),
        },
        {
            field: item => item.answer.student.school ? item.answer.student.school.name : '-',
            label: window.gettext('School'),
        },
        {
            field: item => item.answer.student.city ? item.answer.student.city.name : '-' ,
            label: window.gettext('City'),
        },
        {
            field: item => item.answer.student.city ? item.answer.student.city.region_display : '-',
            label: window.gettext('Region'),
        },
    ]

    filtersSelector = createSelector.bind(this)(
        [
            () => {
                let indexes = this.props.location.query.index || [];
                if (!(indexes instanceof Array))
                    indexes = [indexes];
                return  _.map(indexes, index => Number.parseInt(index));
            },
            () => this.props.store.getIn(['regionsTreeFilter']),
            () => this.props.store.getIn(['citiesTreeFilter']),
            () => this.props.store.getIn(['schoolsTreeFilter']),
            () => this.props.store.getIn(['gradesTreeFilter']),
            () => this.props.store.getIn(['examinationsGradesTreeFilter']),
            () => this.props.store.getIn(['examinationsTreeFilter']),
        ],
        indexes => [
            {
                field: 'marker',
                label: window.gettext('Marker'),
                component: <div style={{fontSize: '.8em'}}>
                    <Tree
                        store={this.props.store.get('markersTreeFilter')}
                        readonly
                        header
                        resource='marker'
                        itemLabel={item => `${item.firstname} ${item.lastname} (${item.username})`}
                        label={window.gettext('Marker')}
                    />
                </div>,
                selected: this.props.store.getIn(['markersTreeFilter', 'selected']).toJS(),
                remove: id => this.context.store.dispatch(removeItemSelection(
                    id,
                    'marker',
                    this.props.store.getIn(['markersTreeFilter', 'elementId']),
                )),
            },
            {
                field: 'region',
                label: window.gettext('Region'),
                component: <div style={{fontSize: '.8rem'}}>
                    <Tree
                        store={this.props.store.get('regionsTreeFilter')}
                        readonly
                        header
                        resource='region'
                        itemLabel='name'
                        label={window.gettext('Region')}
                    />
                </div>,
                selected: this.props.store.getIn(['regionsTreeFilter', 'selected']).toJS(),
                remove: id => this.context.store.dispatch(removeItemSelection(
                    id,
                    'region',
                    this.props.store.getIn(['regionsTreeFilter', 'elementId']),
                )),
            },
            {
                field: 'city',
                label: window.gettext('City'),
                component: <div style={{fontSize: '.8rem'}}>
                    <Tree
                        store={this.props.store.get('citiesTreeFilter')}
                        readonly
                        header
                        resource='city'
                        itemLabel={city => `${city.region_display} - ${city.name}`}
                        filter={{
                            'region': this.props.store.getIn(['regionsTreeFilter', 'selected']).keySeq().join(',') || -1,
                        }}
                        label={window.gettext('City')}
                    />
                </div>,
                selected: this.props.store.getIn(['citiesTreeFilter', 'selected']).toJS(),
                remove: id => this.context.store.dispatch(removeItemSelection(
                    id,
                    'city',
                    this.props.store.getIn(['citiesTreeFilter', 'elementId']),
                )),
            },
            {
                field: 'school',
                label: window.gettext('School'),
                component: <div style={{fontSize: '.8rem'}}>
                    <Tree
                        store={this.props.store.get('schoolsTreeFilter')}
                        readonly
                        header
                        resource='school'
                        itemLabel={school => `${school.city.name} - ${school.name}`}
                        filter={{
                            'city': this.props.store.getIn(['citiesTreeFilter', 'selected']).keySeq().join(',') || -1,
                        }}
                        label={window.gettext('School')}
                    />
                </div>,
                selected: this.props.store.getIn(['schoolsTreeFilter', 'selected']).toJS(),
                remove: id => this.context.store.dispatch(removeItemSelection(
                    id,
                    'school',
                    this.props.store.getIn(['schoolsTreeFilter', 'elementId']),
                )),
            },
            {
                field: 'grade',
                label: window.gettext('Grade'),
                component: <div style={{fontSize: '.8rem'}}>
                    <Tree
                        store={this.props.store.get('gradesTreeFilter')}
                        readonly
                        header
                        resource='grade'
                        label={window.gettext('Grade')}
                    />
                </div>,
                selected: this.props.store.getIn(['gradesTreeFilter', 'selected']).toJS(),
                remove: id => this.context.store.dispatch(removeItemSelection(
                    id,
                    'grade',
                    this.props.store.getIn(['gradesTreeFilter', 'elementId']),
                )),
            },
            {
                label: window.gettext('Examination Grade'),
                component: <div style={{fontSize: '.8rem'}}>
                    <Tree
                        store={this.props.store.get('examinationsGradesTreeFilter')}
                        readonly
                        header
                        resource='grade'
                        label={window.gettext('Examination Grade')}
                    />
                </div>,
                selected: this.props.store.getIn(['examinationsGradesTreeFilter', 'selected']).toJS(),
                remove: id => this.context.store.dispatch(removeItemSelection(
                    id,
                    'grade',
                    this.props.store.getIn(['examinationsGradesTreeFilter', 'elementId']),
                )),
            },
            {
                label: window.gettext('Examination'),
                component: <div style={{fontSize: '.8rem'}}>
                    <Tree
                        store={this.props.store.get('examinationsTreeFilter')}
                        readonly
                        header
                        resource='examination'
                        itemLabel={examination => _.map(
                            examination.grades,
                            grade => grade.title,
                        ).join(window.gettext(',') + ' ') + ' - ' + examination.title}
                        filter={{
                            grades: this.props.store.getIn(['examinationsGradesTreeFilter', 'selected']).keySeq().join(',') || -1,
                        }}
                        label={window.gettext('Examination Grade')}
                    />
                </div>,
                selected: this.props.store.getIn(['examinationsTreeFilter', 'selected']).toJS(),
                remove: id => this.context.store.dispatch(removeItemSelection(
                    id,
                    'examination',
                    this.props.store.getIn(['examinationsTreeFilter', 'elementId']),
                )),
            },
            {
                field: 'index',
                label: window.gettext('Question Index'),
                component: <span>
                    {_.map(
                        Array(20),
                        (__, index) => <div
                            style={{
                                display: 'block',
                                float: 'right',
                                width: '10em',
                                cursor: 'pointer',
                            }}
                            key={index}>
                            <Checkbox
                                checked={_.includes(indexes, index)}
                                onChange={this.indexChanged.bind(
                                    this,
                                    index,
                                )}>
                                {(index + 1).persian()}
                            </Checkbox>
                        </div>,
                    )}
                </span>,
                selected: _.mapValues(
                    _.mapKeys(indexes, _.identity),
                    index => (index +1).persian(),
                ),
                remove: id => this.indexChanged(Number(id)),
            },
        ],
    )

    sorts = [
        {name: 'id', label: window.gettext('ID'), reversed: true},
        {name: 'marker', label: window.gettext('national id')},
        {name: 'national_id', label: window.gettext('national id')},
        {name: 'firstname', label: window.gettext('firstname')},
        {name: 'lastname', label: window.gettext('lastname')},
        {name: 'grade', label: window.gettext('Grade')},
        {name: 'school', label: window.gettext('School')},
        {name: 'city', label: window.gettext('City')},
        {name: 'region', label: window.gettext('Region')},
    ]

    actionsSelector = createSelector(
        [
            () => this.props.actions,
            () => this.props.readonly,
            () => this.props.store.getIn(['paginator', 'selected']).size,
        ],
        () => this.props.readonly
            ? this.props.actions
            : this.props.store.getIn(['paginator', 'selected']).size === 0
                ? this.props.actions
                : _.concat(this.props.actions, [
                    {
                        label: window.gettext('Remove'),
                        action: async () => {
                            if (!window.confirm(window.interpolate(
                                window.gettext('Are you sure you wanna remove these %(count)s results?'),
                                {count: this.props.store.getIn(['paginator', 'selected']).size.persian()},
                                true,
                            )))
                                return;
                            await this.context.store.dispatch(request({
                                path: '/result/?' + queryString.stringify({
                                    id: this.props.store.getIn(['paginator', 'selected']).keySeq().join(','),
                                }),
                                method: 'DELETE',
                            }));
                            this.context.store.dispatch([
                                ...this.props.store.getIn(['paginator', 'selected']).map((__, id) => removeItem(
                                    id,
                                    this.props.store.getIn(['paginator', 'elementId']),
                                )).toArray(),
                                clearSelection(this.props.store.getIn(['paginator', 'elementId'])),
                            ]);
                        },
                    },
                ]),
    )

    componentDidUpdate(prevProps) {
        if (prevProps.params.id && !this.props.params.id) {
            this.context.store.dispatch(paginatorRefresh(this.props.store.getIn(['paginator', 'elementId'])));
        }
    }

    indexChanged(index, event) {
        event.stopPropagation();
        let indexes = this.props.location.query.index || [];
        if (!(indexes instanceof Array))
            indexes = [indexes];
        indexes = _.map(indexes, index => Number.parseInt(index));
        if (_.includes(indexes, index)) {
            _.remove(indexes, s => s === index);
            this.context.store.dispatch(push({
                ...this.props.location,
                query: {...this.props.location.query, index: indexes},
            }));
        }
        else {
            this.context.store.dispatch(push({
                ...this.props.location,
                query: {
                    ...this.props.location.query,
                    index: [...indexes, index],
                },
            }));
        }
    }

    render() {
        if (this.props.params.id)
            return <Mark admin id={this.props.params.id}/>;
        return <PaginatedContainer
            store={this.props.store.get('paginator')}
            resource='result'
            style={this.style}
            listFields={this.listFields}
            tooltip={item => item.id}
            title={window.gettext('Results')}
            selectable
            filters={this.filtersSelector(this.props.store, this.props.location)}
            sorts={this.sorts}
            pageSize={this.props.pageSize}
            actions={this.actionsSelector(this.props.store)}
            buttonActions={this.props.actions}
        />;
    }
}
window.pgettext('answer', 'Status-0');
window.pgettext('answer', 'Status-1');
window.pgettext('answer', 'Status-2');
window.pgettext('answer', 'Status-3');
